import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Filter,
  Package,
  TrendingUp,
  DollarSign,
  FileText,
  Layers,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useStockValuation } from "@/hooks/useStockValuation";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const StockValuationReport = () => {
  const { formatCost, format: formatDecimal, formatTotalAmount } = useDecimalPrecision();
  // Filters
  const [reportDate, setReportDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [layerDateFrom, setLayerDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return format(date, "yyyy-MM-dd");
  });
  const [layerDateTo, setLayerDateTo] = useState(format(new Date(), "yyyy-MM-dd"));

  // Pagination for FIFO Cost Layer Details
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data, isLoading, error } = useStockValuation({
    reportDate,
    layerDateFrom,
    layerDateTo,
  });

  const handleResetFilters = () => {
    setReportDate(format(new Date(), "yyyy-MM-dd"));
    const date = new Date();
    date.setDate(1);
    setLayerDateFrom(format(date, "yyyy-MM-dd"));
    setLayerDateTo(format(new Date(), "yyyy-MM-dd"));
  };

  // Calculate summary statistics
  const summary = useMemo(() => {
    if (!data?.valuationDetails) return { totalItems: 0, totalQuantity: 0, totalValue: 0 };

    return {
      totalItems: data.valuationDetails.length,
      totalQuantity: data.valuationDetails.reduce((sum, item) => sum + item.balance, 0),
      totalValue: data.valuationDetails.reduce((sum, item) => sum + item.totalCost, 0),
    };
  }, [data]);

  // Pagination calculations for cost layers
  const { paginatedCostLayers, totalPages, startItem, endItem, totalItems } = useMemo(() => {
    if (!data?.costLayers) {
      return {
        paginatedCostLayers: [],
        totalPages: 0,
        startItem: 0,
        endItem: 0,
        totalItems: 0,
      };
    }

    const total = data.costLayers.length;
    const pages = Math.ceil(total / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const end = Math.min(start + itemsPerPage, total);
    const paginated = data.costLayers.slice(start, end);

    return {
      paginatedCostLayers: paginated,
      totalPages: pages,
      startItem: total > 0 ? start + 1 : 0,
      endItem: end,
      totalItems: total,
    };
  }, [data?.costLayers, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [layerDateFrom, layerDateTo]);

  if (isLoading) return <LoadingIndicator />;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Error Loading Stock Valuation Report</p>
          <p className="text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Stock Valuation Report</h1>
        <p className="text-sm text-muted-foreground mt-1">
          View inventory valuation based on FIFO costing method
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Items</p>
                <h3 className="text-2xl font-bold mt-2">{summary.totalItems}</h3>
                <p className="text-xs text-muted-foreground mt-1">Active inventory items</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Quantity</p>
                <h3 className="text-2xl font-bold mt-2">
                  {summary.totalQuantity.toLocaleString()}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">Units in stock</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Value</p>
                <h3 className="text-2xl font-bold mt-2">
                  RM {formatTotalAmount(summary.totalValue)}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">FIFO valuation</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Report Filters</span>
              <p className="text-sm text-muted-foreground">
                Configure report parameters and filters
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="reportDate">Report Date</Label>
                <Input
                  type="date"
                  id="reportDate"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                />
              </div>
              <div className="md:col-span-3 flex items-end">
                <Button variant="outline" size="sm" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground border-t pt-4">
              <p>
                <strong>Print Date:</strong> {format(new Date(), "dd/MM/yyyy")} |
                <strong> Page:</strong> 1 |
                <strong> As On Date:</strong> {format(new Date(reportDate), "yyyy-MM-dd")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stock Valuation Details (FIFO Method) */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <div>
              <CardTitle>Stock Valuation Details (FIFO Method)</CardTitle>
              <CardDescription>
                Detailed inventory valuation based on First In, First Out costing
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data?.valuationDetails && data.valuationDetails.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50 dark:bg-slate-800">
                    <TableRow>
                      <TableHead>Item Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead colSpan={4} className="text-center border-l">QTY</TableHead>
                      <TableHead colSpan={2} className="text-center border-l">COST</TableHead>
                    </TableRow>
                    <TableRow className="bg-slate-100 dark:bg-slate-700">
                      <TableHead></TableHead>
                      <TableHead></TableHead>
                      <TableHead className="text-center border-l">B/F</TableHead>
                      <TableHead className="text-center">IN</TableHead>
                      <TableHead className="text-center">OUT</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right border-l">Per Qty.</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.valuationDetails.map((item) => (
                      <TableRow key={item.itemCode}>
                        <TableCell className="font-medium">{item.itemCode}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-center border-l">{formatDecimal(item.broughtForward)}</TableCell>
                        <TableCell className="text-center text-green-600">
                          {formatDecimal(item.totalIn)}
                        </TableCell>
                        <TableCell className="text-center text-red-600">
                          {formatDecimal(item.totalOut)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatDecimal(item.balance)}
                        </TableCell>
                        <TableCell className="text-right border-l">
                          {formatCost(item.costPerQty)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatDecimal(item.totalCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableHeader className="bg-slate-100 dark:bg-slate-700">
                    <TableRow>
                      <TableHead colSpan={2} className="font-bold">TOTAL</TableHead>
                      <TableHead className="text-center border-l font-bold">
                        {formatDecimal(data.valuationDetails.reduce((sum, item) => sum + item.broughtForward, 0))}
                      </TableHead>
                      <TableHead className="text-center font-bold text-green-600">
                        {formatDecimal(data.valuationDetails.reduce((sum, item) => sum + item.totalIn, 0))}
                      </TableHead>
                      <TableHead className="text-center font-bold text-red-600">
                        {formatDecimal(data.valuationDetails.reduce((sum, item) => sum + item.totalOut, 0))}
                      </TableHead>
                      <TableHead className="text-right font-bold">
                        {formatDecimal(summary.totalQuantity)}
                      </TableHead>
                      <TableHead className="border-l"></TableHead>
                      <TableHead className="text-right font-bold">
                        {formatDecimal(summary.totalValue)}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">No stock valuation data</p>
              <p className="text-sm mt-2">There are no stock transactions for the selected date</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FIFO Cost Layer Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            <div className="flex-1">
              <CardTitle>FIFO Cost Layer Details</CardTitle>
              <CardDescription>
                View cost layers for each item in inventory (default: current month)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date Range Filter for Cost Layers */}
          <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4" />
              <span className="font-semibold text-sm">Cost Layer Date Range</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="layerDateFrom">From Date</Label>
                <Input
                  type="date"
                  id="layerDateFrom"
                  value={layerDateFrom}
                  onChange={(e) => setLayerDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="layerDateTo">To Date</Label>
                <Input
                  type="date"
                  id="layerDateTo"
                  value={layerDateTo}
                  onChange={(e) => setLayerDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Cost Layer Details by Item */}
          {data?.costLayers && data.costLayers.length > 0 ? (
            <div className="space-y-6">
              {/* Item count */}
              <div className="text-sm text-muted-foreground">
                Showing {startItem} to {endItem} of {totalItems} items
              </div>

              {/* Paginated items */}
              {paginatedCostLayers.map((item) => (
                <div key={item.itemCode} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-4">
                    {item.itemCode} - {item.description}
                  </h3>
                  <Table>
                    <TableHeader className="bg-slate-50 dark:bg-slate-800">
                      <TableRow>
                        <TableHead>Purchase Date</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                        <TableHead className="text-right">Layer Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {item.layers.map((layer, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{format(new Date(layer.purchaseDate), "dd/MM/yyyy")}</TableCell>
                          <TableCell className="text-right">{formatDecimal(layer.quantity)}</TableCell>
                          <TableCell className="text-right">{formatCost(layer.unitCost)}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatDecimal(layer.layerValue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableHeader className="bg-slate-100 dark:bg-slate-700">
                      <TableRow>
                        <TableHead className="font-bold">Total</TableHead>
                        <TableHead className="text-right font-bold">
                          {formatDecimal(item.layers.reduce((sum, layer) => sum + layer.quantity, 0))}
                        </TableHead>
                        <TableHead className="text-right font-bold">
                          Avg: {formatCost(item.averageUnitCost)}
                        </TableHead>
                        <TableHead className="text-right font-bold">
                          {formatDecimal(item.totalValue)}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </div>
              ))}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t pt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        const showPage =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1);

                        const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                        const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                        if (showEllipsisBefore || showEllipsisAfter) {
                          return (
                            <span key={page} className="px-2 text-muted-foreground">
                              ...
                            </span>
                          );
                        }

                        if (!showPage) return null;

                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-lg font-medium">No cost layer data</p>
              <p className="text-sm mt-2">
                There are no stock purchases for the selected date range
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
